/**
 * Browser-side Sentry scrubbing.
 *
 * The OAuth callback carries the whole token bundle in its query
 * (`/auth/callback?success=true&tokens=<access + refresh JSON>`), and the
 * SDK records page URLs in `request.url` and in navigation / fetch
 * breadcrumbs for the rest of the SPA session. `sendDefaultPii: false` does
 * not touch URLs. So: no query string or fragment ever leaves the browser in
 * an event — the path is enough to locate a fault, and every secret this app
 * has ever put in a URL lived after the `?`.
 *
 * Structural types on purpose: the SDK's `ErrorEvent` / `Breadcrumb` are
 * interfaces, and these functions must accept them without the module
 * depending on the SDK's type surface.
 */
type RequestLike = {
  url?: string
  query_string?: unknown
  data?: unknown
  cookies?: unknown
  headers?: { [key: string]: string }
}

type BreadcrumbLike = {
  data?: { [key: string]: unknown }
}

/** A span on a transaction: `description` is the URL for browser-metrics and `METHOD url` for http.client. */
type SpanLike = {
  description?: string
  data?: { [key: string]: unknown }
}

type EventLike = {
  request?: RequestLike
  breadcrumbs?: BreadcrumbLike[]
  /** Transactions only (`beforeSendTransaction`); absent on error events. */
  spans?: SpanLike[]
  contexts?: { trace?: { data?: { [key: string]: unknown } } }
}

/**
 * Data keys the SDK fills with URLs: breadcrumbs (navigation, fetch, xhr),
 * spans (`url`, `http.url`) and the trace context.
 */
const URL_DATA_KEYS = ['url', 'http.url', 'from', 'to', 'referrer', 'href'] as const

/** Span data the SDK splits OUT of the URL — the query itself, raw. */
const QUERY_DATA_KEYS = ['http.query', 'http.fragment'] as const

/** Never useful in an error report; each has carried a credential before. Matched case-insensitively. */
const SENSITIVE_HEADERS = ['authorization', 'cookie', 'x-api-key'] as const

/** The browser SDK's own request headers are `Referer` + `User-Agent`; the former is a URL. */
const REFERER_HEADERS = new Set(['referer', 'referrer'])

/** Origin + path only. Values that are not URLs pass through unchanged. */
export function stripQuery(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return value
  const cut = value.search(/[?#]/)
  return cut === -1 ? value : value.slice(0, cut)
}

/** URL-valued keys stripped, raw query keys dropped; everything else untouched. */
function scrubUrlData(data: { [key: string]: unknown } | undefined): void {
  if (!data) return
  for (const key of URL_DATA_KEYS) {
    const value = data[key]
    if (typeof value === 'string') data[key] = stripQuery(value)
  }
  for (const key of QUERY_DATA_KEYS) delete data[key]
}

/**
 * A span description is a URL (`browser.*` metrics carry the page URL), a
 * `METHOD url` (http.client), or free text (`ui.click` selectors — which may
 * contain `#`). Only the URL inside it loses its query; text passes through.
 */
export function stripQueryInDescription(value: string | undefined): string | undefined {
  if (typeof value !== 'string') return value
  if (/^\/[^\s]*/.test(value)) return stripQuery(value)
  return value.replace(/https?:\/\/[^\s]+/g, (url) => stripQuery(url) as string)
}

/** `beforeBreadcrumb`: scrub at capture time so the trail never holds a query. */
export function scrubClientBreadcrumb<T extends BreadcrumbLike>(crumb: T): T {
  scrubUrlData(crumb?.data)
  return crumb
}

/** One span of a transaction — see stripQueryInDescription. */
export function scrubClientSpan<T extends SpanLike>(span: T): T {
  if (typeof span.description === 'string') {
    span.description = stripQueryInDescription(span.description)
  }
  scrubUrlData(span.data)
  return span
}

/**
 * `beforeSend` AND `beforeSendTransaction`: last line before the wire —
 * request URL, query, body, cookies, headers, breadcrumbs, and on a
 * transaction every span and the trace context. Transactions go through a
 * different hook, and until 2026-09-14 that hook was not wired: the pageload
 * transaction shipped the page URL, tokens and all, in `request.url` and in
 * eight browser-metrics span descriptions while every error event was clean.
 */
export function scrubClientEvent<T extends EventLike>(event: T): T {
  const request = event.request
  if (request) {
    delete request.data
    delete request.cookies
    delete request.query_string
    if (typeof request.url === 'string') request.url = stripQuery(request.url)
    if (request.headers) {
      for (const name of Object.keys(request.headers)) {
        const lower = name.toLowerCase()
        if ((SENSITIVE_HEADERS as readonly string[]).includes(lower)) {
          delete request.headers[name]
        } else if (REFERER_HEADERS.has(lower)) {
          // The previous page's URL — on the navigation right after the OAuth
          // callback that is the tokens URL (2026-09-14 capture-server pass).
          request.headers[name] = stripQuery(request.headers[name]) as string
        }
      }
    }
  }
  if (Array.isArray(event.breadcrumbs)) {
    for (const crumb of event.breadcrumbs) scrubClientBreadcrumb(crumb)
  }
  if (Array.isArray(event.spans)) {
    for (const span of event.spans) scrubClientSpan(span)
  }
  scrubUrlData(event.contexts?.trace?.data)
  return event
}
